import * as Yup from "yup";
import { Helper } from "../../utils/Helpers";
import { OCCUPANCY_STATUSES } from "../../utils/Constants";
const helper = new Helper();

const ArrivalFormConfig = (
  roomTypes,
  dueAmount,
  passportIdType,
  bookedRooms,
  reservedRooms,
  showOTAField,
  t,
  stayTypes,
  hotelOverrides
) =>
  Yup.object().shape({
    nationality: Yup.string().required("Nationality is required"),
    mobile: Yup.string()
      .required("Mobile number is required")
      .min(7, "Please enter a valid number")
      .test("is-valid-mobile", "Please enter a valid number", function (value) {
        const { nationality } = this.parent;
        if (nationality === "indian") {
          return /^[1-9][0-9]{9}$/.test(value);
        }
        return true;
      }),
    altranate_mobile: Yup.string().notRequired(),
    email: Yup.string()
      .notRequired()
      .transform((value) => (value === "" ? null : value))
      .test(
        "is-valid-email",
        "Please enter a valid email address",
        function (value) {
          if (!value) {
            return true; 
          }
          return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value); // Validate email format
        }
      ),
    proof_no:Yup.string().notRequired(),
    proof_type_id: Yup.number()
      .transform((value) => (Number.isNaN(value) ? null : value))
      .required("Please select the ID Type"),
    passport_no: Yup.string().notRequired(),
    name: Yup.string().required("Please enter your full name"),
    country_id: Yup.string()
      .required("Please select the country")
      .test("is-valid-country", "Invalid country selected", function (value) {
        return !!value;
      }),
    state_id: Yup.string()
      .required("Please select the state")
      .test("is-valid-state", "Invalid state selected", function (value) {
        return !!value;
      }),
    city_id: Yup.string()
      .required("Please select the city")
      .test("is-valid-city", "Invalid city selected", function (value) {
        return !!value;
      }),
    address: Yup.string().required("Please enter the address"),
    occupation: Yup.string().notRequired(),
    enquiry_source_id: Yup.number()
      .transform((value) => (Number.isNaN(value) ? null : value))
      .required("Please select booking source"),
    ota_id: showOTAField
      ? Yup.number()
          .transform((value) => (Number.isNaN(value) ? null : value))
          .nullable()
          .required("Please select the OTA")
      : Yup.number()
          .transform((value) => (Number.isNaN(value) ? null : value))
          .nullable(),
    purpose_of_visit: Yup.string().notRequired(),
    booked_rooms: Yup.array().of(
      Yup.lazy((value, schema) => {
        return Yup.object().shape({
          stay_type_id: Yup.number()
            .transform((value) => (Number.isNaN(value) ? null : value))
            .required("Stay type is required"),
          checkin_date: Yup.date()
            .required("Check in date must be a valid date")
            .typeError("Check in date must be a valid date")
            .test({
              name: "valid-date",
              message: "Check in date must be a valid date",
              test: (value) => helper.isValidDate(value),
            }),
          expected_checkout_date: Yup.date()
            .required("Check out date must be a valid date")
            .typeError("Check out date must be a valid date")
            .test({
              name: "valid-date",
              message: "Check out date must be a valid date",
              test: (value) => helper.isValidDate(value),
            })
            .test({
              name: "checkout-after-checkin",
              message: "Check out date must be greater than check in date",
              test: function (value) {
                const { checkin_date } = this.parent;
                return !checkin_date || !value || value > checkin_date;
              },
            }),
          room_type_id: Yup.number()
            .transform((value) => (Number.isNaN(value) ? null : value))
            .required("Room type is required"),
          room_id: Yup.number()
            .transform((value) => (Number.isNaN(value) ? null : value))
            .required("Room Number is required")
            .test(
              "is-unique-room-id",
              "Room number should be unique",
              function (value) {
                const filteredRoomIds = bookedRooms.filter(
                  (room) =>
                    !bookedRooms.some(
                      (r) =>
                        r.room_id === room.room_id &&
                        r.occupancy_status_code ===
                          OCCUPANCY_STATUSES.ROOM_SHIFT
                    )
                );
                const duplicateRooms = filteredRoomIds.filter(
                  (room, _, arr) =>
                    arr.filter((r) => r.room_id === room.room_id).length > 1
                );
                return (
                  duplicateRooms?.filter((room) => room?.room_id === value)
                    ?.length === 0
                );
              }
            )
            .test(
              "is-room-available",
              "This room is already reserved on these dates",
              function (value) {
                if (!value) {
                  return true;
                }

                if (!reservedRooms) return true;

                // Check if the room is reserved
                return !reservedRooms.some((room) => room.room_id === value);
              }
            ),
          room_type_name: Yup.string(),
          room_no: Yup.string(),
          room_rent: Yup.number()
            .transform((value) => (Number.isNaN(value) ? null : value))
            .required("Room rent is required"),
          rate_plan_id: Yup.number()
            .transform((value) => (Number.isNaN(value) ? null : value))
            .required("Rate Plan is required"),
          no_of_adults: Yup.number()
            .transform((value) => (Number.isNaN(value) ? null : value))
            .required("Number of adults is required")
            .min(1, "Minimum of one adult should be selected")
            .max(50, "Adult count cannot exceed 50"),
          no_of_children: Yup.number()
            .transform((value) => (Number.isNaN(value) ? null : value))
            .notRequired(),
          checkin_grace_hour: Yup.number()
            .nullable()
            .transform((value) => (Number.isNaN(value) ? null : value))
            .min(0, "Minimum allowed: 0 hours")
            .test(
              "is-valid-grace-hours",
              t("pages.booking_form.alert.exceeds_allowed_grace_hours"),
              function (value) {
                if (!value) return true;

                const { checkin_date, expected_checkout_date, stay_type_id, stay_type_code } = this.parent;
                if (!checkin_date || !expected_checkout_date) return true;

                const stayTypeObj = (stayTypes || []).find((st) => Number(st.stay_type_id) === Number(stay_type_id) || st.stay_type_code === stay_type_code);
                const code = stayTypeObj?.stay_type_code?.toLowerCase();

                const staydata = helper.getStayCalculation(
                  checkin_date,
                  expected_checkout_date,
                  stay_type_code,
                  stayTypeObj?.standard_checkin_time,
                  stayTypeObj?.standard_checkout_time
                );
                const systemGraceHrs = staydata?.checkinGraceLabel ? Number(staydata.checkinGraceLabel) : 0;

                if (systemGraceHrs === 0) {
                  return this.createError({
                    message: t("pages.booking_form.alert.no_grace_hours_allowed") || "No grace hours allowed",
                  });
                }

                if (code === "standard") {
                  const maxAllowedCheckinGrace = stayTypeObj?.maximum_checkin_grace ?? 5;
                  const effectiveMax = Math.min(systemGraceHrs, maxAllowedCheckinGrace);
                  if (value > effectiveMax) {
                    if (value > maxAllowedCheckinGrace) {
                      return this.createError({
                        message: t("pages.booking_form.alert.maximum_grace_hour_reached") || "Maximum grace hour reached",
                      });
                    } else {
                      return this.createError({
                        message: t("pages.booking_form.alert.max_allowed_grace_hours", {
                          maxGraceHours: effectiveMax,
                        }) || `Max allowed ${effectiveMax} hours`,
                      });
                    }
                  }
                } else {
                  if (value > systemGraceHrs) {
                    return this.createError({
                      message: t("pages.booking_form.alert.max_allowed_grace_hours", {
                        maxGraceHours: systemGraceHrs,
                      }) || `Max allowed ${systemGraceHrs} hours`,
                    });
                  }
                }
                return true;
              }
            ),
        });
      })
    ),
    payment_type_id: Yup.string().test(
      "is-payment-mode-required",
      "Payment mode is required",
      function (value) {
        const { paid_amount } = this.parent;
        return paid_amount !== undefined && paid_amount !== null
          ? !!value
          : true;
      }
    ),
    paid_amount: Yup.number()
      .transform((value) => (Number.isNaN(value) ? null : value))
      .notRequired(),
    total_paid_amount: Yup.string().transform((value) =>
      Number.isNaN(value) ? null : value
    ),
    remarks: Yup.string().notRequired(),
    receipt_requested: Yup.string(),
    invoice_name: Yup.string()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .test(
        "company-name-required",
        "Company name is required",
        function (value) {
          return this.parent.billing_type === "company"
            ? !!(value && value.trim())
            : true;
        }
      ),
    gstin_number: Yup.string()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .test("gstin-format", "Please enter a valid GSTIN", function (value) {
        if (!value) return true;
        return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(value);
      })
      .test(
        "gstin-required-indian",
        "GSTIN is required for Indian companies",
        function (value) {
          const { billing_type, nationality } = this.parent;
          if (billing_type === "company" && nationality === "indian") {
            return !!value;
          }
          return true;
        }
      ),
    invoice_address: Yup.string().nullable().notRequired(),
    invoice_mobile: Yup.string()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .test(
        "company-mobile-valid",
        "Please enter a valid company mobile number",
        function (value) {
          if (this.parent.billing_type !== "company") return true;
          if (!value) return true;
          return this.parent.nationality === "indian"
            ? /^[6-9]\d{9}$/.test(value)
            : /^\d{7,15}$/.test(value);
        }
      ),
    gender: Yup.string().nullable().notRequired(),
    special_note: Yup.string()
      .nullable()
      .notRequired()
      .matches(/^[A-Za-z0-9 ]*$/, "Special Note must be alphanumeric only"),
    additional_guests: Yup.array()
      .test(
        "primary-guest-required",
        "Please select a primary guest",
        function (guests) {
          if (!guests || guests.length === 0) return true;
          const filled = guests.filter((g) => g?.name && g.name.trim());
          if (filled.length === 0) return true;
          return filled.some((g) => !!g.is_primary);
        },
      )
      .of(
      Yup.object().shape({
        name: Yup.string()
          .transform((value) => (value === "" ? undefined : value))
          .when(
            ["gender", "address", "country_id", "state_id", "city_id", "phone_number"],
            {
              is: (gender, address, country_id, state_id, city_id, phone_number) =>
                !!gender ||
                !!address ||
                !!country_id ||
                !!state_id ||
                !!city_id ||
                !!phone_number,
              then: (schema) =>
                schema.required("Guest name is required"),
              otherwise: (schema) => schema.notRequired(),
            },
          ),
        phone_number: Yup.string()
          .nullable()
          .notRequired()
          .transform((value) => (value === "" ? null : value))
          .test("phone-format", "Please enter a valid phone number", function (value) {
            if (!value) return true;
            const nationality = this.from?.[1]?.value?.nationality;
          if (nationality === "indian") {
          return /^\d{10}$/.test(value);
          }
            return /^\d{7,15}$/.test(value);
          }),
        gender: Yup.string()
          .nullable()
          .notRequired()
          .transform((value) => (value === "" ? null : value))
          .test("valid-gender", "Please select a valid gender", function (value) {
            if (!value) return true;
            return ["Male", "Female", "Other"].includes(value);
          }),
        address: Yup.string()
          .nullable()
          .notRequired()
          .transform((value) => (value === "" ? null : value))
          .test("address-length", "Address must be at least 3 characters", function (value) {
            if (!value) return true;
            return value.trim().length >= 3;
          }),
        country_id: Yup.string()
          .nullable()
          .notRequired()
          .transform((value) => (value === "" ? null : value))
          .test("valid-country", "Invalid country selected", function (value) {
            if (!value) return true;
            return !!value;
          }),
        state_id: Yup.string()
          .nullable()
          .notRequired()
          .transform((value) => (value === "" ? null : value))
          .test("valid-state", "Invalid state selected", function (value) {
            if (!value) return true;
            return !!value;
          }),
        city_id: Yup.string()
          .nullable()
          .notRequired()
          .transform((value) => (value === "" ? null : value))
          .test("valid-city", "Invalid city selected", function (value) {
            if (!value) return true;
            return !!value;
          }),
      }),
    ),
  });

export default ArrivalFormConfig;
